import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Switch, TextField } from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { ImageCropUpload } from '@/components/imageCropUpload';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { useBranchForm } from './useBranchForm';

type BranchFormModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useBranchForm>;
    isOpen: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
};

const BRANCH_IMAGE_CROP_SIZE = { width: 512, height: 512 };

export const BranchFormModalView: React.FC<BranchFormModalViewProps> = ({
    error,
    form,
    isOpen,
    isSubmitting,
    mode,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const {
        image,
        isValid,
        name,
        address,
        area,
        isMain,
        showAreaError,
        onImageChange,
        onNameChange,
        onAddressChange,
        onAreaChange,
        onAreaBlur,
        onIsMainChange,
        onSubmit,
    } = form;

    const copy = React.useMemo(
        () => ({
            title:
                mode === 'edit'
                    ? t('branches.components.branchTable.form.editTitle')
                    : t('branches.components.branchTable.form.title'),
            description: t('branches.components.branchTable.form.description'),
            imageLabel: t('branches.components.branchTable.form.imageLabel'),
            imageDescription: t(
                'branches.components.branchTable.form.imageDescription',
            ),
            imageSelect: t('branches.components.branchTable.form.imageSelect'),
            imageChange: t('branches.components.branchTable.form.imageChange'),
            imageRemove: t('branches.components.branchTable.form.imageRemove'),
            imageApply: t('branches.components.branchTable.form.imageApply'),
            imageUploadLabel: t(
                'branches.components.branchTable.form.imageUploadLabel',
            ),
            imageInvalidType: t(
                'branches.components.branchTable.form.imageInvalidType',
            ),
            imageInvalidSize: t(
                'branches.components.branchTable.form.imageInvalidSize',
            ),
            imageCropError: t(
                'branches.components.branchTable.form.imageCropError',
            ),
            imageReadError: t(
                'branches.components.branchTable.form.imageReadError',
            ),
            nameLabel: t('branches.components.branchTable.form.nameLabel'),
            namePlaceholder: t(
                'branches.components.branchTable.form.namePlaceholder',
            ),
            addressLabel: t(
                'branches.components.branchTable.form.addressLabel',
            ),
            addressPlaceholder: t(
                'branches.components.branchTable.form.addressPlaceholder',
            ),
            areaLabel: t('branches.components.branchTable.form.areaLabel'),
            areaPlaceholder: t(
                'branches.components.branchTable.form.areaPlaceholder',
            ),
            areaInvalid: t('branches.components.branchTable.form.areaInvalid'),
            mainLabel: t('branches.components.branchTable.form.mainLabel'),
            mainHelper: t('branches.components.branchTable.form.mainHelper'),
            cancel: t('branches.components.branchTable.form.cancel'),
            submit:
                mode === 'edit'
                    ? t('branches.components.branchTable.form.editSubmit')
                    : t('branches.components.branchTable.form.submit'),
        }),
        [mode, t],
    );

    return (
        <FormDrawer
            footer={
                <>
                    <Button
                        isDisabled={isSubmitting}
                        onPress={onClose}
                        variant="tertiary"
                        {...getNodeTestIdProp('branch-form-cancel')}
                    >
                        {copy.cancel}
                    </Button>
                    <Button
                        isDisabled={!isValid}
                        isPending={isSubmitting}
                        onPress={onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('branch-form-submit')}
                    >
                        {copy.submit}
                    </Button>
                </>
            }
            isOpen={isOpen}
            onClose={onClose}
            subtitle={copy.description}
            testId="branch-form"
            title={copy.title}
        >
            <div
                className="flex flex-col gap-1.5"
                {...getNodeTestIdProp('branch-form-image-field')}
            >
                <Label
                    className="text-sm font-medium text-foreground"
                    {...getNodeTestIdProp('branch-form-image-label')}
                >
                    {copy.imageLabel}
                </Label>
                <ImageCropUpload
                    applyLabel={copy.imageApply}
                    aspectClassName="aspect-square"
                    boxClassName="mx-auto w-[150px]"
                    changeLabel={copy.imageChange}
                    cropErrorMessage={copy.imageCropError}
                    cropSize={BRANCH_IMAGE_CROP_SIZE}
                    description={copy.imageDescription}
                    id="branch-form-image"
                    invalidSizeMessage={copy.imageInvalidSize}
                    invalidTypeMessage={copy.imageInvalidType}
                    label={copy.imageUploadLabel}
                    onChange={onImageChange}
                    readErrorMessage={copy.imageReadError}
                    removeLabel={copy.imageRemove}
                    selectLabel={copy.imageSelect}
                    value={image}
                />
            </div>
            <TextField
                isRequired
                onChange={onNameChange}
                value={name}
                {...getNodeTestIdProp('branch-form-name')}
            >
                <Label>{copy.nameLabel}</Label>
                <Input placeholder={copy.namePlaceholder} />
            </TextField>
            <TextField
                isRequired
                onChange={onAddressChange}
                value={address}
                {...getNodeTestIdProp('branch-form-address')}
            >
                <Label>{copy.addressLabel}</Label>
                <Input placeholder={copy.addressPlaceholder} />
            </TextField>
            <TextField
                onChange={onAreaChange}
                value={area}
                {...getNodeTestIdProp('branch-form-area')}
            >
                <Label>{copy.areaLabel}</Label>
                <Input
                    inputMode="decimal"
                    onBlur={onAreaBlur}
                    placeholder={copy.areaPlaceholder}
                />
            </TextField>
            {showAreaError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('branch-form-area-error')}
                >
                    {copy.areaInvalid}
                </p>
            ) : null}
            <div
                className="flex flex-col gap-1.5"
                {...getNodeTestIdProp('branch-form-main-field')}
            >
                <Switch
                    isSelected={isMain}
                    onChange={onIsMainChange}
                    {...getNodeTestIdProp('branch-form-main')}
                >
                    <Switch.Control>
                        <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Content>
                        <Label className="text-sm font-medium text-foreground">
                            {copy.mainLabel}
                        </Label>
                    </Switch.Content>
                </Switch>
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('branch-form-main-helper')}
                >
                    {copy.mainHelper}
                </p>
            </div>
            {error ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('branch-form-error')}
                >
                    {error}
                </p>
            ) : null}
        </FormDrawer>
    );
};
